import React, { useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { COMPANY_INFO, NAV_LINKS } from '../lib/constants'

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const location = useLocation()

  const closeMobileMenu = () => setMobileMenuOpen(false)

  return (
    <header className="sticky top-0 z-50 w-full bg-[#16182A] shadow-md border-b border-[#252B66]/50">
      {/* Top emergency / quick info bar */}
      <div className="bg-[#121322] text-[#A6ACE2] text-xs py-2 px-4 sm:px-6 border-b border-[#252B66]/40">
        <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-4 sm:gap-6">
            <span className="inline-flex items-center gap-1.5 font-medium text-[#E0E4FF]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A]" aria-hidden="true" />
              <span>25 års erfaring i el-faget</span>
            </span>
            <span className="hidden sm:inline-flex items-center gap-1.5 text-[#9FA6D9]">
              <i className="fa-solid fa-van-shuttle text-[#9FA6D9]" aria-hidden="true" />
              <span>485 kr./time ex. moms inkl. servicevogn</span>
            </span>
            <span className="hidden md:inline-flex items-center gap-1.5 text-[#9FA6D9]">
              <i className="fa-solid fa-shield-check text-[#9FA6D9]" aria-hidden="true" />
              <span>Fuldt forsikret</span>
            </span>
          </div>

          <div className="flex items-center gap-4 ml-auto">
            <a
              href={`mailto:${COMPANY_INFO.email}`}
              className="hidden lg:inline-flex items-center gap-1.5 text-[#9FA6D9] hover:text-white transition-colors whitespace-nowrap"
            >
              <i className="fa-regular fa-envelope" aria-hidden="true" />
              <span>{COMPANY_INFO.email}</span>
            </a>
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              className="inline-flex items-center gap-1.5 font-semibold text-white hover:text-[#E36C0A] transition-colors whitespace-nowrap"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#E36C0A] animate-pulse" aria-hidden="true" />
              <span>Tlf: {COMPANY_INFO.phone}</span>
            </a>
          </div>
        </div>
      </div>

      {/* Main navigation header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo brand */}
          <Link
            to="/"
            onClick={closeMobileMenu}
            className="flex items-center gap-2.5 sm:gap-3 group focus:outline-none focus:ring-2 focus:ring-[#E36C0A] rounded-lg p-1 min-w-0"
          >
            <div className="flex flex-col min-w-0">
              <span className="font-extrabold text-lg sm:text-2xl tracking-tight text-white leading-none group-hover:text-[#E36C0A] transition-colors truncate">
                jakob<span className="text-[#E36C0A]">elektriker</span>.dk
              </span>
            </div>
          </Link>

          {/* Desktop Navigation — STRICT REQUIREMENT: Only visible on >= 1200px */}
          <nav className="hidden min-[1200px]:flex items-center gap-1" aria-label="Hovednavigation">
            {NAV_LINKS.map(link => {
              const isActive =
                link.href === '/'
                  ? location.pathname === '/'
                  : location.pathname === link.href || location.pathname.startsWith(`${link.href}/`)

              return (
                <NavLink
                  key={link.href}
                  to={link.href}
                  className={`px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider transition-all ${
                    isActive
                      ? 'bg-[#252A6E] text-white border border-[#3C449E] shadow-sm'
                      : 'text-[#9FA6D9] hover:text-white hover:bg-[#252A6E]/40'
                  }`}
                >
                  {link.label}
                </NavLink>
              )
            })}
          </nav>

          {/* Mobile CTA + Hamburger Button — STRICT REQUIREMENT: Visible ONLY on < 1200px */}
          <div className="flex min-[1200px]:hidden items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="p-2 sm:p-2.5 text-[#D9D9D5] hover:text-white hover:bg-[#292A30] rounded-lg focus:outline-none focus:ring-2 focus:ring-[#E36C0A] shrink-0"
              aria-expanded={mobileMenuOpen}
              aria-label="Åbn menu"
            >
              <i className={`fa-solid ${mobileMenuOpen ? 'fa-xmark' : 'fa-bars'} text-xl`} aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>

      {/* Mobile drawer / overlay on < 1200px */}
      {mobileMenuOpen && (
        <div className="min-[1200px]:hidden bg-[#16182A] border-t border-[#252B66]/60 shadow-2xl px-4 py-6 space-y-4 max-h-[calc(100vh-5rem)] overflow-y-auto">
          <div className="grid grid-cols-1 gap-1">
            {NAV_LINKS.map(link => {
              const isActive =
                link.href === '/'
                  ? location.pathname === '/'
                  : location.pathname === link.href || location.pathname.startsWith(`${link.href}/`)

              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={closeMobileMenu}
                  className={`flex items-center justify-between px-4 py-3 rounded-xl text-base font-semibold transition-colors ${
                    isActive
                      ? 'bg-[#252A6E] text-white border border-[#3C449E]/40'
                      : 'text-[#DCE0FF] hover:bg-[#1C203A]'
                  }`}
                >
                  <span>{link.label}</span>
                  {isActive && <i className="fa-solid fa-chevron-right text-xs text-[#E36C0A]" aria-hidden="true" />}
                </Link>
              )
            })}
          </div>

          <div className="pt-4 border-t border-[#252B66]/60 space-y-3">
            <a
              href={`tel:${COMPANY_INFO.phoneRaw}`}
              onClick={closeMobileMenu}
              className="flex items-center justify-center gap-2.5 w-full bg-[#E36C0A] hover:bg-[#B95000] text-white font-bold px-4 py-3.5 rounded-xl shadow-sm text-center whitespace-nowrap transition-colors"
            >
              <i className="fa-solid fa-phone" aria-hidden="true" />
              <span>Ring til Jakob – {COMPANY_INFO.phone}</span>
            </a>

            <div className="p-3 bg-[#1C203A] border border-[#2F3670]/50 rounded-xl text-xs text-[#A8B0E6] space-y-1">
              <div className="font-semibold text-white">{COMPANY_INFO.name}</div>
              <div>{COMPANY_INFO.address}, {COMPANY_INFO.postal} {COMPANY_INFO.city}</div>
              <div className="text-[#9FA6D9]">CVR: {COMPANY_INFO.cvr} • Timepris: 485 kr. ex. moms</div>
            </div>
          </div>
        </div>
      )}
    </header>
  )
}
